import {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext
} from "react";
import Icon from "@mdi/react";
import { mdiChevronDown, mdiChevronUp } from "@mdi/js";
import clsx from "clsx";

type CardCtx = {
  partialTargetId?: string;
  togglePartial: () => void;
  isTargetCollapsed: boolean;
};

const CollapsibleCtx = createContext<CardCtx | null>(null);

interface CardProps {
  title: string;
  id: string;
  defaultOpen?: boolean; // whole card open/close (kept if you still want it)
  titleClassName?: string;
  right?: React.ReactNode;
  maxHeight?: number;
  /** Which inner Part to toggle when clicking header */
  partialTargetId?: string; // e.g., "your-fee"
  /** Start that Part collapsed */
  partialDefaultCollapsed?: boolean;
  displayArrow?: boolean;
}

function useMeasureExpand(ref: React.RefObject<HTMLElement>, expanded: boolean) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // animate max-height for smooth open/close
    el.style.overflow = "hidden";
    el.style.transition = "max-height 200ms ease";
    if (expanded) {
      el.style.maxHeight = "none";
      const h = el.scrollHeight;
      el.style.maxHeight = "0px";
      // force reflow
      void el.offsetHeight;
      el.style.maxHeight = `${h}px`;
      const done = () => (el.style.maxHeight = "none");
      el.addEventListener("transitionend", done, { once: true });
    } else {
      // collapse from current height
      const h = el.scrollHeight;
      el.style.maxHeight = `${h}px`;
      void el.offsetHeight;
      el.style.maxHeight = "0px";
    }
  }, [expanded, ref]);
}

export default function CollapsibleCard({
  title,
  titleClassName,
  children,
  defaultOpen = true,
  right,
  id,
  maxHeight,
  partialTargetId,
  partialDefaultCollapsed = false,
  displayArrow = true
}: PropsWithChildren<CardProps>) {
  const [open, setOpen] = useState(defaultOpen);
  const [targetCollapsed, setTargetCollapsed] = useState(!!partialDefaultCollapsed);

  const ctxValue = useMemo<CardCtx>(
    () => ({
      partialTargetId,
      isTargetCollapsed: !!targetCollapsed,
      togglePartial: () => setTargetCollapsed((v) => !v)
    }),
    [partialTargetId, targetCollapsed]
  );

  const handleHeaderClick = () => {
    if (partialTargetId) {
      setTargetCollapsed((v) => !v);
    } else {
      setOpen((v) => !v);
    }
  };

  return (
    <CollapsibleCtx.Provider value={ctxValue}>
      <div
        className={clsx("shadow-lg rounded-lg border-card bg-card", open && "open")}
        id={id}
        data-collapsible-card>
        <div className="w-full flex items-center justify-between px-3 py-2">
          <button
            type="button"
            className="flex-1 text-left flex items-center"
            onClick={handleHeaderClick}
            aria-controls={partialTargetId ?? id}
            aria-expanded={partialTargetId ? !targetCollapsed : open}
            data-expand-button>
            <span className={clsx(titleClassName, "text-primary font-semibold text-sm")}>
              {title}
            </span>
          </button>
          {displayArrow && (
            <div className="flex items-center gap-2">
              {right}
              <button
                onClick={handleHeaderClick}
                className="text-primary hover:text-primary-dark transition-colors"
                aria-label={
                  partialTargetId
                    ? targetCollapsed
                      ? "Expand section"
                      : "Collapse section"
                    : open
                      ? "Collapse card"
                      : "Expand card"
                }>
                <Icon
                  path={
                    partialTargetId
                      ? targetCollapsed
                        ? mdiChevronDown
                        : mdiChevronUp
                      : open
                        ? mdiChevronUp
                        : mdiChevronDown
                  }
                  size={1}
                  className="text-placeholder"
                />
              </button>
            </div>
          )}
        </div>

        {open ? (
          <div
            className={clsx("px-3 pb-3", maxHeight ? "overflow-y-auto" : "")}
            style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}>
            {children}
          </div>
        ) : (
          <div className="hidden" />
        )}
      </div>
    </CollapsibleCtx.Provider>
  );
}

/** Collapsible inner section */
type PartProps = {
  id: string; // must match partialTargetId to be controlled by header
  defaultCollapsed?: boolean; // optional: local default if used standalone later
  className?: string;
};

function PartImpl({
  id,
  defaultCollapsed = false,
  className,
  children
}: PropsWithChildren<PartProps>) {
  const ctx = useContext(CollapsibleCtx);
  const controlled = ctx?.partialTargetId === id;
  const collapsed = controlled ? ctx.isTargetCollapsed : defaultCollapsed;
  const ref = useRef<HTMLDivElement>(null);

  useMeasureExpand(ref, !collapsed);

  return (
    <div
      id={id}
      ref={ref}
      aria-hidden={collapsed}
      className={className}
      // when uncontrolled, still give it initial styles for animation
      style={{ overflow: "hidden" }}
      data-collapsible-part="">
      {children}
    </div>
  );
}

// attach as a static subcomponent
CollapsibleCard.Part = PartImpl as unknown as (props: PropsWithChildren<PartProps>) => JSX.Element;
